import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts';
import styled from 'styled-components';
import { green, red, blue, orange, white } from 'utils/style/colors';
import { CustomizedAxisTick } from '../../Chart/SurveyChart';
import { convertDigitToProperFormat } from '../../../utils/number';
import { ITemplateReportValues } from 'store/templates/templatesSlice';
import { ITemplateReportOptions } from 'store/tasks/tasksType';
import i18n from '../../../i18n';

const ChartReport = styled.div`
  direction: ltr;
  margin-top: 2em;
  width: ${(props) => (props.width ? props.width : '100%')};
`;

interface IReportChartProps {
  type: 'main' | 'secondary' | 'verify' | 'assignee';
  onClickOnBar: (type: string) => (chartNode: any) => void;
  templateReportOptions: ITemplateReportOptions;
  chartData: ITemplateReportValues[];
  hasTooltip?: boolean;
  showRelative: boolean;
  height?: number;
  printable?: boolean;
  width?: string;
}

const LineCustomizedLabel = (props: any) => {
  const { x, y, value } = props;
  const radius = 10;
  const width = 60;
  return (
    <g>
      <rect
        width={width}
        height={20}
        x={x - width / 2}
        y={value < 90 ? y - 40 : y + 20}
        rx={radius}
        strokeWidth="1px"
        stroke={white}
        fill={blue}
      />
      <text
        x={x}
        y={value < 90 ? y - 30 : y + 30}
        fill={white}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {convertDigitToProperFormat(value) +
          i18n.t('percent', { ns: 'taskManager' })}
      </text>
    </g>
  );
};

class ReportChart extends React.PureComponent<IReportChartProps> {
  static defaultProps = {
    hasTooltip: true,
    printable: false,
  };

  render() {
    const {
      type,
      onClickOnBar,
      templateReportOptions: tro,
      chartData,
      hasTooltip,
      showRelative,
    } = this.props;
    const addIfNotNull = (
      templateReportOptions: any,
      key: string,
      array: { key: string; color: string }[],
      color: string
    ) => {
      if (templateReportOptions[key]) array.push({ key, color });
      return array;
    };

    const keySuffix = showRelative
      ? ` (${i18n.t('percentage', {
          ns: 'taskManager',
        })})`
      : '';

    const barKeys = [] as { key: string; color: string }[];
    const lineKeys = [] as { key: string; color: string }[];
    if (type === 'main') {
      addIfNotNull(tro, 'completedTaskCountMain', barKeys, green);
      addIfNotNull(tro, 'inProgressTaskCountMain', barKeys, orange);
      addIfNotNull(tro, 'notStartedTaskCountMain', barKeys, red);
      addIfNotNull(tro, 'averageProgressMain', lineKeys, blue);
    }
    if (type === 'secondary') {
      addIfNotNull(tro, 'completedTaskCountSecondary', barKeys, green);
      addIfNotNull(tro, 'completedTaskCountSecondary', barKeys, orange);
      addIfNotNull(tro, 'notStartedTaskCountSecondary', barKeys, red);
      addIfNotNull(tro, 'averageProgressSecondary', lineKeys, blue);
    }
    if (type === 'verify') {
      addIfNotNull(tro, 'completedTaskCountVerifier', barKeys, green);
      addIfNotNull(tro, 'rejectedTaskCountVerifier', barKeys, orange);
      addIfNotNull(tro, 'notStartedTaskCountVerifier', barKeys, red);
    }
    if (type === 'assignee') {
      addIfNotNull(tro, 'completedAssignmentCount', barKeys, green);
      addIfNotNull(tro, 'inProgressAssignmentCount', barKeys, orange);
      addIfNotNull(tro, 'notStartedAssignmentCount', barKeys, red);
      addIfNotNull(tro, 'averageAssignmentProgress', lineKeys, blue);
    }
    const axisHeight = chartData.reduce((previousValue, currentValue) => {
      return Math.max(
        previousValue,
        (currentValue.title?.toString()?.length as number) * 4.1 + 30
      );
    }, 0);
    const height = this.props.printable
      ? 400
      : this.props.height || 300 + axisHeight;

    return (
      <ChartReport width={this.props.width}>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart
            // width={height}
            // height={height}
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="title"
              tick={<CustomizedAxisTick />}
              interval={0}
              tickSize={20}
              height={Math.min(axisHeight, 200)}
            />
            <YAxis yAxisId="left" domain={[0, 100]} />
            <YAxis yAxisId="right" hide domain={[0, 'dataMax']} />
            {hasTooltip && <Tooltip />}
            {Object.values(barKeys).map(({ key, color }) => {
              return (
                <Bar
                  key={key}
                  yAxisId="right"
                  stackId="s1"
                  dataKey={`${tro[key]}${keySuffix}`}
                  fill={color}
                  onClick={onClickOnBar(key)}
                >
                  <LabelList
                    dataKey={key}
                    position="center"
                    className={'white-fill clickTrough'}
                    formatter={(label) => {
                      return convertDigitToProperFormat(label);
                    }}
                  />
                </Bar>
              );
            })}
            {Object.values(lineKeys).map(({ key, color }) => (
              <Line
                key={key}
                yAxisId="left"
                type="monotone"
                dataKey={tro[key]}
                stroke={color}
                activeDot={{
                  r: 6,
                }}
                label={
                  <LineCustomizedLabel
                    dataKey={tro[key]}
                    position="insideTop"
                    offset={20}
                    angle={0}
                  />
                }
              ></Line>
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </ChartReport>
    );
  }
}
export default ReportChart;
